import { usePath } from '@hooks/usePath'
import { getAccessDays } from '@utils/accessDays'
import { useMemo } from 'react'
import { Circle, Text, YStack, XStack } from 'tamagui'
import { HeroIcon } from '@components/HeroIcon'
import { formatDateShort } from '@utils/dateFormatter'
import { PathContinueSection } from './PathContinueSection'
import { PathProductsSection } from './PathProductSection'
import { PathGhostLoading } from './PathGhostLoading'
import { EmptyStateComponent } from '@components/EmptyStateComponent'
import { useTranslation } from 'react-i18next'

export function PathContent() {
  const {
    newEnrollment,
    coursesInPath,
    enrollmentIsLoading,
    onLoadMore,
    hasMore,
  } = usePath()

  const { t } = useTranslation('CourseDetails')
  const pathHasCourses = coursesInPath.length > 0

  const accessDays = useMemo(() => {
    if (!newEnrollment) return null
    return getAccessDays({
      expireDate: newEnrollment.schoolProduct.expireDate,
      activatedAt: newEnrollment.activatedAt,
      availableUntil: newEnrollment.availableUntil,
      availableTimeLength: newEnrollment.schoolProduct.availableTimeLength,
      unlimited: newEnrollment.unlimited,
    })
  }, [newEnrollment])

  const badgeInfo = useMemo(() => {
    return {
      badgeStyle:
        accessDays && accessDays.days <= 7 ? '$danger-300' : '$info-300',
      accessDaysLabel:
        accessDays && accessDays.days <= 7
          ? 'Atenção! acesso acaba em:'
          : 'Acesso liberado até:',
    }
  }, [accessDays])

  if (enrollmentIsLoading) return <PathGhostLoading />

  if (!newEnrollment) return null
  return (
    <YStack marginTop={50} flex={1} backgroundColor="$bg-page">
      {pathHasCourses ? (
        <>
          <YStack
            paddingHorizontal="$spacing-inset-xs"
            paddingVertical="$spacing-inset-sm"
            backgroundColor="$bg-chrome"
            testID="path-content"
            $gtMd={{
              paddingHorizontal: '$spacing-inset-lg',
            }}
          >
            {accessDays && (
              <XStack
                borderRadius="$border-radius-pill"
                padding="$spacing-inset-nano"
                marginBottom="$spacing-inset-xxs"
                bg="$bg-surface-raised"
                alignItems="center"
                alignSelf="flex-start"
                testID="access-days-badge"
              >
                <Circle
                  bg={badgeInfo?.badgeStyle}
                  size="$spacing-md"
                  marginRight={8}
                >
                  <HeroIcon
                    icon={['fal', 'calendar-alt']}
                    size={16}
                    color="$text-on-accent"
                  />
                </Circle>
                <Text
                  color="$text-primary"
                  ff="$mulishBold"
                  fontSize="$font-size-xs"
                  marginRight="$spacing-inset-quarck"
                  testID="access-days-label"
                >
                  {badgeInfo?.accessDaysLabel}
                </Text>
                <Text
                  color={badgeInfo?.badgeStyle}
                  ff="$mulishBold"
                  fontSize="$font-size-xs"
                >
                  {formatDateShort(accessDays.expireDate)}
                </Text>
              </XStack>
            )}
            <Text
              color="$text-primary"
              ff="$mulishBold"
              fontSize="$font-size-md"
              fontWeight={800}
              testID="product-title"
            >
              {newEnrollment?.schoolProduct.title}
            </Text>
          </YStack>
          <YStack
            paddingLeft="$spacing-inset-xs"
            paddingTop="$spacing-inset-md"
            backgroundColor="$bg-page"
            $gtMd={{
              paddingLeft: '$spacing-inset-lg',
            }}
            justifyContent="center"
            gap="$spacing-inset-lg"
            flex={1}
          >
            <PathContinueSection />
            <PathProductsSection
              enrollmentId={newEnrollment.id.toString()}
              newEnrollment={newEnrollment}
              onLoadMore={onLoadMore}
              hasMore={hasMore}
            />
          </YStack>
        </>
      ) : (
        <YStack
          paddingHorizontal="$spacing-inset-xs"
          paddingBottom="$spacing-inset-xxl"
          backgroundColor="$bg-chrome"
          testID="empty-state"
        >
          <EmptyStateComponent
            title={t('emptyState.title')}
            description={t('emptyState.description')}
          />
        </YStack>
      )}
    </YStack>
  )
}
